import { getSalaryMinimumS6202Rules } from './businessRules/SalaryMinimumS6202Rules';
import { hasSuspension } from './businessRules/SuspensionRules';
import { repriseOptionCas1, repriseOptionCas2 } from './StopInformation/reprise';
import { TypeAttestation } from './typeAttestation/index';
import { TypeDeclaration } from './typeChoix';
import { ReasonForWorkStop } from './workStopReason';

export enum CodeCerfa {
    S3201 = 'S3201',
    S6202 = 'S6202',
    S3202 = 'S3202',
}

export enum CodeScenario {
    AT = 'AT',
    AMRP = 'AMRP',
    AMTN = 'AMTN',
    MAL = 'MAL',
    MP = 'MP',
    MAT = 'MAT',
    PAT = 'PAT',
}

export type ScenarioAt = {
    cerfa: string;
    code: string; // Unique code identifying the scenario
    intitule: string; // Intitule of the form
    description: string; // Description of the scenario
    formAtFields?: string[]; // Form work stoppage fields
    repriseOptions?: string[]; // list of option do be dispayed
    displayLegendRightsCase: boolean;
    formsChoix2: string[]; // Forms used for choix 2
};

// Enumeration for different form codes used in the application
export enum FormName {
    DeclarationReason = 'motif',
    TypeDeclaration = 'typeDeclaration',
    Subscriber = 'subscripber',
    Affilie = 'affilie',
    WorkStoppage = 'workStoppage',
    SalaryBaseS6202 = 'salaryBaseS6202',
    SalaryRappelS6202 = 'salaryRappelS6202',
    SalaryIncompletePeriodS6202 = 'salaryIncompletePeriodS6202',
    SalaryBaseS3201 = 'salaryBaseS3201',
    SalaryInterruptionPeriodS3201 = 'SalaryInterruptionPeriodS3201',
    SalaryReference = 'salaryReference',
    Certification = 'certification',
    Subrogation = 'subrogation',
    Interim = 'interim',
}

// List of common forms used across various scenarios
export const commonForm = [FormName.DeclarationReason, FormName.TypeDeclaration, FormName.Affilie, FormName.WorkStoppage, FormName.SalaryReference, FormName.Interim, FormName.Certification];

// Form for choix 2 specific to scenarios AM, MP
const FormChoix2_S6202 = [FormName.Subscriber, FormName.SalaryBaseS6202, FormName.SalaryRappelS6202, FormName.SalaryIncompletePeriodS6202, FormName.Subrogation];
// Form for choix 2 specific to scenarios AMRP, AMTN, MAL, MAT, PAT
const FormChoix2_S3201 = [FormName.Subscriber, FormName.SalaryBaseS3201, FormName.SalaryInterruptionPeriodS3201, FormName.Subrogation];
// Form for choix 2 specific to scenarios MAL +6months
const FormChoix2_S3202 = [];

// Common fields related to work stoppage information
const commonFormAtFields = ['workStoppage.infos.start', 'workStoppage.infos.end', 'workStoppage.infos.lastDayWorked', 'workStoppage.infos.isHospitalized', 'workStoppage.infos.hospitalizedDate'];

const repriseFormAtFields = ['workStoppage.infos.returnToWorkCod', 'workStoppage.infos.returnToWorkDate'];

/**
 * Defines scenariosAT based on combinations of typeAttestation, reasonForWorkStop, and choise.
 * Key format: typeAttestation + reasonForWorkStop + choise
 * Example: '1-1-1' => typeAttestation Initial (1), reasonForWorkStop AccidentTravail (1)
 */
export const scenariosAT: Record<string, ScenarioAt> = {
    '1-AT': {
        cerfa: CodeCerfa.S6202,
        code: TypeAttestation.Initial + CodeScenario.AT,
        intitule: 'Arrêt de travail initial',
        description: 'Nationalité à déclarer dans DA', // TODO :: remove description
        formAtFields: commonFormAtFields.concat(repriseFormAtFields).concat(['workStoppage.infos.accidentDate', 'workStoppage.infos.hasOtherVictims']),
        repriseOptions: repriseOptionCas1,
        displayLegendRightsCase: false,
        formsChoix2: FormChoix2_S6202,
    },
    '1-AMRP': {
        cerfa: CodeCerfa.S3201,
        code: TypeAttestation.Initial + CodeScenario.AMRP,
        intitule: 'Allocation maternités / risque particuliers',
        description: 'DAT spécifique à la maternité',
        formAtFields: commonFormAtFields
            .concat(repriseFormAtFields)
            .concat([
                'workStoppage.rights.workedHours',
                'workStoppage.rights.period',
                'workStoppage.infos.situationAtStopDateCod',
                'workStoppage.rights.start',
                'workStoppage.rights.end',
                'workStoppage.rights.contribution',
            ]),
        repriseOptions: repriseOptionCas2,
        displayLegendRightsCase: false,
        formsChoix2: FormChoix2_S3201,
    },
    '1-AMTN': {
        cerfa: CodeCerfa.S3201,
        code: TypeAttestation.Initial + CodeScenario.AMTN,
        intitule: 'Allocation maternité / Travail de nuit',
        description: "Scénario identique au précédent à l'exception du motif",
        formAtFields: commonFormAtFields
            .concat(repriseFormAtFields)
            .concat([
                'workStoppage.rights.workedHours',
                'workStoppage.rights.period',
                'workStoppage.infos.situationAtStopDateCod',
                'workStoppage.rights.start',
                'workStoppage.rights.end',
                'workStoppage.rights.contribution',
            ]),
        repriseOptions: repriseOptionCas2,
        displayLegendRightsCase: false,
        formsChoix2: FormChoix2_S3201,
    },
    '1-MAL': {
        cerfa: CodeCerfa.S3201,
        code: TypeAttestation.Initial + CodeScenario.MAL,
        intitule: 'Maladie',
        description: "Scénario identique au précédent à l'exception du motif",
        formAtFields: commonFormAtFields
            .concat(repriseFormAtFields)
            .concat([
                'workStoppage.rights.workedHours',
                'workStoppage.rights.period',
                'workStoppage.infos.situationAtStopDateCod',
                'workStoppage.rights.start',
                'workStoppage.rights.end',
                'workStoppage.rights.contribution',
            ]),
        repriseOptions: repriseOptionCas2,
        displayLegendRightsCase: false,
        formsChoix2: FormChoix2_S3201,
    },
    '1-MP': {
        cerfa: CodeCerfa.S6202,
        code: TypeAttestation.Initial + CodeScenario.MP,
        intitule: 'Maladie professionnelle',
        description: 'Nationalité à déclarer dans DA + DAT spécifique à la maladie professionnelle',
        formAtFields: commonFormAtFields.concat(repriseFormAtFields).concat(['workStoppage.infos.firstConstatationDate']),
        repriseOptions: repriseOptionCas1,
        displayLegendRightsCase: false,
        formsChoix2: FormChoix2_S6202,
    },
    '1-MAT': {
        cerfa: CodeCerfa.S3201,
        code: TypeAttestation.Initial + CodeScenario.MAT,
        intitule: 'Maternité',
        description: "Scénario identique au précédent à l'exception du motif + date début congé maternité/paternité",
        formAtFields: commonFormAtFields
            .concat(repriseFormAtFields)
            .concat([
                'workStoppage.infos.parentalLeaveStartDate',
                'workStoppage.rights.workedHours',
                'workStoppage.rights.period',
                'workStoppage.infos.situationAtStopDateCod',
                'workStoppage.rights.start',
                'workStoppage.rights.end',
                'workStoppage.rights.contribution',
            ]),
        repriseOptions: repriseOptionCas2,
        displayLegendRightsCase: false,
        formsChoix2: FormChoix2_S3201,
    },
    '1-PAT': {
        cerfa: CodeCerfa.S3201,
        code: TypeAttestation.Initial + CodeScenario.PAT,
        intitule: 'Paternité',
        description: "Scénario identique à la maternité + date de naissance de l'enfant",
        formAtFields: commonFormAtFields
            .concat(repriseFormAtFields)
            .concat([
                'workStoppage.infos.parentalLeaveStartDate',
                'workStoppage.infos.childDateOfBirth',
                'workStoppage.rights.workedHours',
                'workStoppage.rights.period',
                'workStoppage.infos.situationAtStopDateCod',
                'workStoppage.rights.start',
                'workStoppage.rights.end',
                'workStoppage.rights.contribution',
            ]),
        repriseOptions: repriseOptionCas2,
        displayLegendRightsCase: false,
        formsChoix2: FormChoix2_S3201,
    },
    '2-MAL': {
        cerfa: CodeCerfa.S3202,
        code: TypeAttestation.Over6Months + CodeScenario.MAL,
        intitule: 'Arrêt Maladie de plus de 6 mois',
        description: 'DAT spécifique au arrêt de +6 mois',
        formAtFields: commonFormAtFields.concat([
            'workStoppage.rightsS3202.workedHours1',
            'workStoppage.rightsS3202.workedHours2',
            'workStoppage.rightsS3202.contribution1',
            'workStoppage.rightsS3202.contribution2',
        ]),
        repriseOptions: [],
        displayLegendRightsCase: true,
        formsChoix2: FormChoix2_S3202,
    },
};

// Function to validate and parse string to enum
const parseMotifArret = (value: string): ReasonForWorkStop | undefined => {
    if (!Object.values(ReasonForWorkStop).includes(value as ReasonForWorkStop)) {
        return undefined;
    }
    return value as ReasonForWorkStop;
};

const parseTypeAttestation = (value: string): TypeAttestation | undefined => {
    if (!Object.values(TypeAttestation).includes(value as TypeAttestation)) {
        return undefined;
    }
    return value as TypeAttestation;
};

// Function to define scenariosAT based on typeAttestation, reasonForWorkStop, and choise
export const getScenarioAtByCodes = (store): ScenarioAt => {
    const { typeAttestationCod = null, reasonForWorkStopCod = null } = store || {};

    // Parse and validate parameters
    const TypeAttestation = parseTypeAttestation(typeAttestationCod);
    const motifArret = parseMotifArret(reasonForWorkStopCod);

    if (!TypeAttestation || !motifArret) {
        const missingFields = [];
        if (!TypeAttestation) missingFields.push('TypeAttestation');
        if (!motifArret) missingFields.push('motifArret');

        console.error(`Incomplete data for getting scenario. The following field(s) are missing: ${missingFields.join(', ')}`);
        return null;
    }

    const scenarioKey = `${TypeAttestation}-${motifArret}`;
    const scenario = scenariosAT[scenarioKey];

    if (!scenario) {
        return null;
    }

    return scenario;
};

const filterStoreFields = (store, allowedFields, prefix = '') => {
    if (typeof store !== 'object' || store === null) {
        return;
    }

    for (const key of Object.keys(store)) {
        const fullPath = prefix ? `${prefix}.${key}` : key;

        if (typeof store[key] === 'object' && store[key] !== null) {
            filterStoreFields(store[key], allowedFields, fullPath);
            if (Object.keys(store[key]).length === 0 && !allowedFields.includes(fullPath)) {
                delete store[key];
            }
        } else if (!allowedFields.includes(fullPath)) {
            delete store[key];
        }
    }
};

export const storeCleaner = (store) => {
    try {
        // Set code cerfa
        const codCerfa = getCerfaCodByTypes(store);
        store.cerfa = codCerfa;

        // Scenario
        const scenario = getScenarioAtByCodes(store);
        if (!scenario) return store;

        if (codCerfa !== CodeCerfa.S6202) {
            store.affilie.nationalityCod = null;
        }
        // delete all properties not defined in formAT
        if (store?.workStoppage) {
            filterStoreFields(store.workStoppage, scenario.formAtFields, 'workStoppage');
        }

        if (!scenario.displayLegendRightsCase) {
            // TODO :: Refactor ??
            if (store?.workStoppage?.cotisation?.p12m) delete store.workStoppage.cotisation.p12m;
            if (store?.workStoppage?.cotisation?.p6m) delete store.workStoppage.cotisation.p6m;
        }

        // Documents : remove empty entries
        if (store?.documents) {
            const cleanedDocuments = store.documents.filter((obj) => obj.data !== undefined && obj.filename !== undefined);
            store.documents = [...cleanedDocuments];
        }

        cleanS3201(store);
        cleanS3202(store);
        cleanS6202(store);
        cleanSalaryMinimumS6202(store);
        cleanTPA(store);

        // Choix 2
        //if ((store.choise === TypeDeclaration.Choice1 || !scenario.formsChoix2.includes(FormName.SalaryBaseS3201)) && store?.salaryBase) delete store.salaryBase;
        if ((store.choise === TypeDeclaration.Choice1 || !scenario.formsChoix2.includes(FormName.SalaryBaseS6202)) && store?.salaryBaseS6202) delete store.salaryBaseS6202;
        if ((store.choise === TypeDeclaration.Choice1 || !scenario.formsChoix2.includes(FormName.SalaryIncompletePeriodS6202)) && store?.salaryIncompletePeriod) delete store.salaryIncompletePeriod;
        if ((store.choise === TypeDeclaration.Choice1 || !scenario.formsChoix2.includes(FormName.SalaryRappelS6202)) && store?.salaryRappel) delete store.salaryRappel;
        if ((store.choise === TypeDeclaration.Choice1 || !scenario.formsChoix2.includes(FormName.SalaryInterruptionPeriodS3201)) && store?.salaryRegulCotis) delete store.salaryRegulCotis;

        // TODO :: ajouter condition pour salaryIncompletePeriod.maintienInterruptionActivityS6202 (4 champs)
    } catch (error) {
        console.error('Error while cleaning store:', error);
    }

    return store;
};

const cleanS3201 = (store) => {
    if (store.choise === TypeDeclaration.Choice1 || store.cerfa !== CodeCerfa.S3201) {
        delete store?.salaryBaseS3201;
        delete store?.salaryRappelS3201;
        delete store?.interruptionActivityS3201;
    }
};

const cleanS3202 = (store) => {
    if (store.choise === TypeDeclaration.Choice1 || store.cerfa !== CodeCerfa.S3201) {
    }
};

const cleanS6202 = (store) => {
    if (store.choise === TypeDeclaration.Choice1 || store.cerfa !== CodeCerfa.S6202) {
        delete store?.salaryBaseS6202;
        delete store?.salaryMinimumS6202;
        delete store?.salaryRappelS6202;
        delete store?.interruptionActivityS6202;
        delete store?.maintienInterruptionActivityS6202;
    }
};

const cleanSalaryMinimumS6202 = (store) => {
    // Salary minimum S6202
    const { hasSalaryMinimumContratS6202, displaySalaryMinimumS6202 } = getSalaryMinimumS6202Rules(store);
    if (!hasSalaryMinimumContratS6202) {
        delete store?.salaryMinimumS6202?.contract;
        delete store?.salaryMinimumS6202?.contractDate;
    }
    if (!displaySalaryMinimumS6202) {
        delete store?.salaryMinimumS6202;
    }
};

const cleanTPA = (store) => {
    if (!hasSuspension(store)) {
        delete store?.affilie?.tpa;
        delete store?.suspensions;
    }
};

/**
 *
 *
 * @param {array} fields - The field to check for.
 * @param {string} form - The form to check within.
 * @param {object} store - The store containing the forms.
 * @returns {boolean} - True if the field exists in the form, otherwise false.
 */
export const formHasField = (fields, form, store) => {
    try {
        const scenario = getScenarioAtByCodes(store);

        if (!scenario) {
            console.error('Scenario is not defined or empty.');
            return false;
        }

        if (typeof scenario[form] === 'undefined') {
            console.error(`Form '${form}' is not defined in scenario.`);
            return false;
        }
        //return Array.isArray(scenario[form]) && scenario[form].includes(field);
        return fields.some((field) => scenario[form].includes(field));
    } catch (error) {
        console.error('Error defining field presence:', error);
        return false;
    }
};

// Mapping of typeAttestation and reasonForWorkStop to corresponding CodeCerfa
export const getCerfaCodByTypes = (store) => {
    const mapping: Record<string, Partial<Record<CodeScenario, CodeCerfa>>> = {
        '1': {
            [CodeScenario.AT]: CodeCerfa.S6202,
            [CodeScenario.AMRP]: CodeCerfa.S3201,
            [CodeScenario.AMTN]: CodeCerfa.S3201,
            [CodeScenario.MAL]: CodeCerfa.S3201,
            [CodeScenario.MP]: CodeCerfa.S6202,
            [CodeScenario.MAT]: CodeCerfa.S3201,
            [CodeScenario.PAT]: CodeCerfa.S3201,
        },
        '2': {
            [CodeScenario.MAL]: CodeCerfa.S3202,
        },
    };

    const typeAttestationCod = store.typeAttestationCod;
    const reasonForWorkStopCod = store.reasonForWorkStopCod;

    const cerfaCode = mapping[typeAttestationCod]?.[reasonForWorkStopCod] ?? null;

    if (cerfaCode === null) {
        throw new Error(`No valid cerfa code found for typeAttestation: ${typeAttestationCod}, reasonForWorkStop: ${reasonForWorkStopCod}`);
    }

    return cerfaCode;
};
